// A small remark plugin that turns [[Wiki Links]] and relative markdown
// links into links the reader can intercept.
//
// Two kinds of reference are recognised:
//
//   [[Some Note]]            a wikilink — the name of another document
//   [text](notes/other.md)   a relative path — a link to a sibling document
//
// Both resolve against the server's document index (path -> id, and title ->
// id), so a click opens the right document IN the app instead of leaving the
// app. A link that does not resolve stays plain text — a dead link must not
// pretend to be a working one.
//
// The plugin emits ORDINARY mdast "link" nodes whose url carries the
// document id in the "doc:" scheme:
//
//   { type: "link", url: "doc:SYSTEM_notes_beta.md", ... }
//
// That matters: mdast-util-to-hast drops node types it does not know, so a
// made-up "doclink" node would simply vanish from the rendered page. A
// standard link survives the whole pipeline, and the reader's "a"
// component turns a "doc:" url into an in-app button.
//
// Hand-rolled on purpose: no editor and no link plugin ships in P1. The walk
// is a plain recursive pass over the mdast tree, which is all a read-only
// reader needs.

// The scheme that marks a link as pointing at another document in the
// library rather than at a URL.
export const DOC_SCHEME = "doc:";

// A wikilink: [[Name]] or [[Name|display text]]. The name may not contain
// brackets or a pipe.
const WIKILINK = /\[\[([^[\]|\n]+?)(?:\|([^[\]\n]+?))?\]\]/g;

// A markdown link whose target is a relative path (not a URL, not a
// fragment). `foo.md`, `./foo.md`, `../notes/foo.md`.
const RELATIVE = /^\.{0,2}\/[^\s]*\.md$|^\.{0,2}[^\s:]+\.md$/;

export const isRelativeDocUrl = (url) =>
  typeof url === "string" &&
  RELATIVE.test(url) &&
  !/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url) &&
  !url.startsWith("#");

// The reference inside a markdown link, or null when it is not one.
const mdLinkTarget = (node) => {
  if (node.type !== "link") return null;
  return isRelativeDocUrl(node.url) ? node.url : null;
};

// A relative path resolves against the document's own folder, so "other.md"
// next to "notes/thing.md" means "notes/other.md" — the way a link in a file
// on disk behaves.
export const joinPath = (base, rel) => {
  const clean = rel.replace(/^\.\//, "");
  if (!base) return clean;
  if (clean.startsWith("/")) return clean.slice(1);
  const dir = base.split("/").slice(0, -1).join("/");
  const parts = (dir ? `${dir}/${clean}` : clean).split("/");
  const out = [];
  for (const part of parts) {
    if (part === "." || part === "") continue;
    if (part === "..") out.pop();
    else out.push(part);
  }
  return out.join("/");
};

// The url a resolved document link carries.
export const docUrl = (docId) => `${DOC_SCHEME}${docId}`;

// Pull the document id back out of a "doc:" url.
export const docIdFromUrl = (url) => {
  if (typeof url !== "string" || !url.startsWith(DOC_SCHEME)) return null;
  return url.slice(DOC_SCHEME.length);
};

/**
 * Build the plugin.
 *
 * @param {object} options
 * @param {(ref: string) => (string | null)} options.resolve
 *   Given a reference ("Some Note" or "notes/other.md"), return the
 *   document id it points at, or null when it does not resolve.
 * @param {string} [options.basePath]
 *   The path of the document being rendered — a relative link is resolved
 *   against it.
 */
export function remarkDocLinks(options = {}) {
  const { resolve, basePath = "" } = options;

  const tryResolve = (ref) => {
    if (typeof resolve !== "function") return null;
    try {
      return resolve(ref) || null;
    } catch {
      return null;
    }
  };

  // Split a text node around its wikilinks. Returns null when the node has
  // no wikilink worth turning into a link.
  const splitWikilinks = (node) => {
    const value = node.value || "";
    if (!value.includes("[[")) return null;
    const pieces = [];
    let cursor = 0;
    let changed = false;
    WIKILINK.lastIndex = 0;
    let m = WIKILINK.exec(value);
    while (m) {
      const [whole, name, label] = m;
      const id = tryResolve(name.trim());
      if (id) {
        if (value.slice(cursor, m.index)) {
          pieces.push({ type: "text", value: value.slice(cursor, m.index) });
        }
        pieces.push({
          type: "link",
          url: docUrl(id),
          data: { docId: id, docRef: name.trim() },
          children: [{ type: "text", value: (label || name).trim() }],
        });
        cursor = m.index + whole.length;
        changed = true;
      }
      m = WIKILINK.exec(value);
    }
    if (!changed) return null;
    if (value.slice(cursor)) {
      pieces.push({ type: "text", value: value.slice(cursor) });
    }
    return pieces;
  };

  // One recursive pass: links are rewritten in place, text nodes are split.
  const walk = (node) => {
    if (!node || !Array.isArray(node.children)) return;
    const out = [];
    for (const child of node.children) {
      const target = mdLinkTarget(child);
      if (target) {
        const id = tryResolve(joinPath(basePath, target));
        if (id) {
          out.push({
            type: "link",
            url: docUrl(id),
            data: { docId: id, docRef: target },
            children: [
              { type: "text", value: child.children?.[0]?.value || target },
            ],
          });
          continue;
        }
      }
      if (child.type === "text") {
        const pieces = splitWikilinks(child);
        if (pieces) {
          out.push(...pieces);
          continue;
        }
      }
      walk(child);
      out.push(child);
    }
    node.children = out;
  };

  return (tree) => walk(tree);
}

export default remarkDocLinks;
